import { SelectionModel } from '@angular/cdk/collections';
import {
  Component,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SnackbarService } from 'src/app/layout/snackbars/snackbar.service';
import { Airline, Contact, Status } from 'src/app/shared/shared.models';
import { AirlineSettingsService } from '../../airline-settings/airline-settings.service';
import { GSA } from '../gsa-settings.model';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatProgressBar } from '@angular/material/progress-bar';
import { DialogStructureComponent } from '../../../layout/dialog-structure/dialog-structure.component';
import { MatFormField, MatLabel, MatInput, MatError, MatPrefix, MatSuffix } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatDivider } from '@angular/material/divider';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, MatExpansionPanelDescription } from '@angular/material/expansion';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatCheckbox } from '@angular/material/checkbox';

@Component({
    selector: 'app-gsa-settings-dialog',
    templateUrl: './gsa-settings-dialog.component.html',
    styleUrls: ['./gsa-settings-dialog.component.scss'],
    imports: [MatProgressBar, DialogStructureComponent, FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatError, MatSelect, MatOption, MatDivider, MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, MatExpansionPanelDescription, MatButton, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatIconButton, MatIcon, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatPaginator, MatPrefix, MatSuffix, MatCheckbox]
})
export class GsaSettingsDialogComponent implements OnInit {
  @ViewChild('paginator') paginator: MatPaginator;
  @ViewChild('airlinePaginator') airlinePaginator: MatPaginator;
  gsa: GSA;
  data: any;

  contactsTableSource = new MatTableDataSource();
  contactsdata: Contact[] = [];
  displayedContactColumns: string[] = [
    'contactName',
    'contactMail',
    'contactPhone',
    'actions',
  ];
  contactsForm: UntypedFormGroup;
  searchForm: UntypedFormGroup;
  gsaForm: UntypedFormGroup;

  spinning = false;     // for mat-progressbar in the dialog -> spinns while airlines are being fetched
  airlinesTableSource = new MatTableDataSource();
  airlinesData: Airline[] = [];
  displayedAirlineColumns: string[] = ['select', 'id', 'airlineName', 'IATA'];
  initialSelection: any[] = [];
  selection = new SelectionModel<Airline>(true, []);

  status: Status[] = [
    { value: 0, viewValue: 'inactive' },
    { value: 1, viewValue: 'active' },
  ];

  constructor(
    private dialogRef: MatDialogRef<GsaSettingsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data,
    private airlineSettingsService: AirlineSettingsService,
    private snackbarService: SnackbarService
  ) {
    this.data = data;
  }

  ngOnInit(): void {
    this.gsaForm = new UntypedFormGroup({
      status: new UntypedFormControl(null, [Validators.required]),
      gsaName: new UntypedFormControl(null, [Validators.required]),
    });

    if (this.data) {
      this.gsa = this.data;
      this.setFormValues(this.data);
      this.getContacts();
    }
    this.getAirlines();
    this.contactsForm = new UntypedFormGroup({
      contactName: new UntypedFormControl(null, Validators.required),
      contactMail: new UntypedFormControl(null, [
        Validators.required,
        Validators.email,
      ]),
      phoneNumber: new UntypedFormControl(null, Validators.required),
    });

    this.searchForm = new UntypedFormGroup({
      filter: new UntypedFormControl(''),
    });
  }

  setFormValues(gsa: GSA): void {
    this.gsaForm.patchValue(gsa);
  }

  submitEmitter(): void {
    // when gsa existing -> update
    if (this.gsa) {
      this.gsa.contacts = this.contactsdata;

      this.gsa = Object.assign(this.gsa, this.gsaForm.value);
    } else {
      this.gsa = this.gsaForm.value;
      this.gsa.contacts = this.contactsdata;
      // add to table
    }
    this.saveAirlines();
    this.dialogRef.close(this.gsa);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  // --- CONTACTS ---
  addContact(): void {
    let c: Contact = {
      contactMail: this.contactsForm.controls.contactMail.value,
      contactPerson: this.contactsForm.controls.contactName.value,
      phoneNumber: this.contactsForm.controls.phoneNumber.value,
    };
    c.id = Math.floor(Math.random() * (500 - 0 + 1)) + 0;
    this.contactsdata.push(c);

    this.contactsTableSource = new MatTableDataSource(this.contactsdata);
    this.contactsTableSource.paginator = this.paginator;
  }

  getContacts(): void {
    if (this.gsa.contacts == null) {
      this.gsa.contacts = [];
    }
    this.contactsdata = this.gsa.contacts;
    this.contactsTableSource = new MatTableDataSource(this.contactsdata);
    this.contactsTableSource.paginator = this.paginator;
  }

  deleteContact(index: number): void {
    this.contactsdata.splice(index, 1);
    this.contactsTableSource = new MatTableDataSource(this.contactsdata);
  }

  // check if the contacts table is empty
  contactsValiadtion(): boolean {
    if (this.contactsdata.length === 0) {
      return false;
    }
    return true;
  }
  // -- AIRLINES ---
  getAirlines(): void {
    this.spinning = true;
    this.airlineSettingsService.fetchAirlines().subscribe(
      (data) => {
        this.airlinesData = data;

        // when there is a GSA (edit), check if airlines are already selected in the database and check them in the table
        if (this.gsa && this.gsa.airlines) {
          this.checkAirlines();
        }

        this.airlinesTableSource = new MatTableDataSource(this.airlinesData);
        this.airlinesTableSource.paginator = this.airlinePaginator;
        this.spinning = false;
      },

      (error: any) => {
        this.spinning = false;
        this.snackbarService.openError(error.status);
      }
    );
  }

  checkAirlines(): void {
    let selection: any[] = [];
    this.gsa.airlines.forEach((gsaAirline) => {
      this.airlinesData.forEach((airline) => {
        if (gsaAirline.id === airline.id) {
          selection.push(airline);
        }
      });
    });

    this.initialSelection = selection;
    this.selection = new SelectionModel<Airline>(true, this.initialSelection);
  }

  saveAirlines(): void {
    this.gsa.airlines = this.selection.selected;
  }



  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.airlinesTableSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle(): void {
    this.isAllSelected()
      ? this.selection.clear()
      : this.airlinesTableSource.data.forEach((row: any) =>
          this.selection.select(row)
        );
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${
      row.id + 1
    }`;
  }

  // -- search --
  clearSearch(): void {
    this.searchForm.controls['filter'].setValue('');
    this.applyFilter();
  }

  applyFilter(): void {
    const filterValue = this.searchForm.value.filter;
    this.airlinesTableSource.filter = filterValue.trim().toLowerCase();
    this.airlinesTableSource.paginator = this.airlinePaginator;
  }
}
